import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Popover,
} from "@mui/material";
import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { Comment, CommentReaction } from "../types";

interface CommentPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  nodeId: string;
  comments: Comment[];
  onAddComment: (nodeId: string, text: string) => void;
  onAddCommentReaction: (
    nodeId: string,
    commentId: string,
    emoji: string
  ) => void;
  onRemoveCommentReaction: (
    nodeId: string,
    commentId: string,
    emoji: string,
    userId: string
  ) => void;
  currentUserId: string;
  currentUserName: string;
  currentUserColor: string;
}

const CommentPopover: React.FC<CommentPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  nodeId,
  comments,
  onAddComment,
  onAddCommentReaction,
  onRemoveCommentReaction,
  currentUserId,
}) => {
  const [newComment, setNewComment] = useState("");
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null
  );
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Auto focus on text field when popover opens
  useEffect(() => {
    if (open && textFieldRef.current) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleAddComment = useCallback(() => {
    if (newComment.trim()) {
      onAddComment(nodeId, newComment.trim());
      setNewComment("");
    }
  }, [nodeId, newComment, onAddComment]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        handleAddComment();
      }
    },
    [handleAddComment]
  );

  const handleEmojiClick = useCallback(
    (commentId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedCommentId(commentId);
      setEmojiAnchor(e.currentTarget as HTMLElement);
    },
    []
  );

  const handleEmojiClose = useCallback(() => {
    setEmojiAnchor(null);
    setSelectedCommentId(null);
  }, []);

  const handleEmojiSelect = useCallback(
    (emojiData: EmojiClickData) => {
      if (selectedCommentId) {
        onAddCommentReaction(nodeId, selectedCommentId, emojiData.emoji);
      }
      handleEmojiClose();
    },
    [nodeId, selectedCommentId, onAddCommentReaction, handleEmojiClose]
  );

  const handleReactionClick = useCallback(
    (commentId: string, emoji: string, userHasReacted: boolean) => {
      if (userHasReacted) {
        onRemoveCommentReaction(nodeId, commentId, emoji, currentUserId);
      } else {
        onAddCommentReaction(nodeId, commentId, emoji);
      }
    },
    [nodeId, onAddCommentReaction, onRemoveCommentReaction, currentUserId]
  );

  const getGroupedReactions = (reactions: CommentReaction[]) => {
    const grouped: { [emoji: string]: CommentReaction[] } = {};
    reactions.forEach((reaction) => {
      if (!grouped[reaction.emoji]) {
        grouped[reaction.emoji] = [];
      }
      grouped[reaction.emoji].push(reaction);
    });
    return grouped;
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return "Just now";
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{ zIndex: 1500 }}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        keepMounted={false}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Paper sx={{ width: 350, maxHeight: 400, overflow: "hidden" }}>
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: "14px", fontWeight: 600 }}
              >
                Comments ({comments.length})
              </Typography>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Comments List */}
            <Box sx={{ maxHeight: 250, overflow: "auto", p: 1 }}>
              {comments.length === 0 ? (
                <Box
                  sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
                >
                  <Typography variant="body2">No comments yet</Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {[...comments] // Create a shallow copy before sorting
                    .sort((a, b) => a.timestamp - b.timestamp)
                    .map((comment) => {
                      const groupedReactions = getGroupedReactions(
                        comment.reactions || []
                      );
                      return (
                        <Paper
                          key={comment.id}
                          variant="outlined"
                          sx={{ p: 2, backgroundColor: "#fafafa" }}
                        >
                          {/* Comment Header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: "12px",
                                bgcolor: comment.userColor,
                                mr: 1,
                              }}
                            >
                              {comment.userName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, fontSize: "12px" }}
                            >
                              {comment.userName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                ml: "auto",
                                color: "text.secondary",
                                fontSize: "10px",
                              }}
                            >
                              {formatTimestamp(comment.timestamp)}
                            </Typography>
                          </Box>

                          {/* Comment Text */}
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontSize: "13px", lineHeight: 1.4 }}
                          >
                            {comment.text}
                          </Typography>

                          {/* Comment Actions */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleEmojiClick(comment.id, e)}
                              sx={{ padding: "2px" }}
                            >
                              <EmojiIcon sx={{ fontSize: "14px" }} />
                            </IconButton>

                            {/* Comment Reactions */}
                            {Object.entries(groupedReactions).map(
                              ([emoji, reactions]) => {
                                const userHasReacted = reactions.some(
                                  (reaction) =>
                                    reaction.userId === currentUserId
                                );
                                return (
                                  <Chip
                                    key={emoji}
                                    label={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <span style={{ fontSize: "10px" }}>
                                          {emoji}
                                        </span>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "9px" }}
                                        >
                                          {reactions.length}
                                        </Typography>
                                      </Box>
                                    }
                                    onClick={() =>
                                      handleReactionClick(
                                        comment.id,
                                        emoji,
                                        userHasReacted
                                      )
                                    }
                                    size="small"
                                    variant={
                                      userHasReacted ? "filled" : "outlined"
                                    }
                                    color={
                                      userHasReacted ? "primary" : "default"
                                    }
                                    sx={{
                                      height: 18,
                                      fontSize: "9px",
                                      cursor: "pointer",
                                      "& .MuiChip-label": {
                                        padding: "0 4px",
                                      },
                                    }}
                                  />
                                );
                              }
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Add Comment Section */}
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  ref={textFieldRef}
                  fullWidth
                  multiline
                  maxRows={3}
                  size="small"
                  placeholder="Add a comment... (Ctrl+Enter to send)"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={handleKeyPress}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "13px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Paper>
        </div>
      </Popover>

      {/* Emoji Picker for Comment Reactions */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={handleEmojiClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 1600 }}
        disableEnforceFocus={true}
        disableAutoFocus={true}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Box>
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              width={300}
              height={350}
              searchDisabled={false}
              skinTonesDisabled={true}
              previewConfig={{
                showPreview: false,
              }}
            />
          </Box>
        </div>
      </Popover>
    </>
  );
};

export default CommentPopover;
